const WHATSAPP_NAME_PATTERN = /^[a-z0-9_]+$/;
const VARIABLE_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

export const MESSAGE_TEMPLATE_CHANNELS = ['whatsapp', 'sms', 'email'];
export const MESSAGE_TEMPLATE_CATEGORIES = ['utility', 'marketing', 'authentication'];
export const MESSAGE_TEMPLATE_STATUSES = ['draft', 'pending', 'approved', 'rejected', 'paused', 'disabled'];
export const MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET = 'message-template-headers';

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function toJsonObject(value, fallback = {}) {
    return isPlainObject(value) ? value : fallback;
}

function toButtonArray(value) {
    return Array.isArray(value) ? value : [];
}

function normalizeVariables(variables) {
    if (Array.isArray(variables)) {
        return variables.reduce((acc, entry, index) => {
            const key = normalizeText(entry?.key || entry?.name || entry?.number || String(index + 1));
            if (!key) return acc;
            acc[key] = {
                sample: normalizeText(entry?.sample || entry?.sampleValue || entry?.value),
                source: normalizeText(entry?.source || entry?.fillFrom || 'manual') || 'manual',
            };
            return acc;
        }, {});
    }
    if (!isPlainObject(variables)) return {};
    return Object.entries(variables).reduce((acc, [key, value]) => {
        if (isPlainObject(value)) {
            acc[String(key)] = {
                sample: normalizeText(value.sample || value.sampleValue || value.value),
                source: normalizeText(value.source || value.fillFrom || 'manual') || 'manual',
            };
        } else {
            acc[String(key)] = { sample: normalizeText(value), source: 'manual' };
        }
        return acc;
    }, {});
}

export function resolveTemplateStatus({ channel, status }) {
    const normalizedChannel = normalizeText(channel).toLowerCase();
    const normalizedStatus = normalizeText(status).toLowerCase();
    if (normalizedChannel === 'sms' || normalizedChannel === 'email') {
        return 'approved';
    }
    if (MESSAGE_TEMPLATE_STATUSES.includes(normalizedStatus)) {
        return normalizedStatus;
    }
    return 'draft';
}

export function shouldAllowMessageTemplateSyncRequest({ userValid = false, isAdmin = false, tokenVerified = false } = {}) {
    if (userValid) {
        return Boolean(isAdmin);
    }
    return Boolean(tokenVerified);
}

export function listVariablesInTemplate({ body = '', header = {}, footer = '', buttons = [] } = {}) {
    const sources = [String(body || ''), String(footer || '')];
    if (normalizeText(header?.type).toLowerCase() === 'text') {
        sources.push(String(header?.text || ''));
    }
    toButtonArray(buttons).forEach((button) => {
        if (normalizeText(button?.type).toLowerCase() === 'url') {
            sources.push(String(button?.url || ''));
            sources.push(String(button?.dynamicSuffix || button?.dynamic_suffix || ''));
        }
    });

    const keys = new Set();
    sources.forEach((source) => {
        for (const match of source.matchAll(VARIABLE_PATTERN)) {
            keys.add(match[1]);
        }
    });
    return [...keys];
}

function validateConsecutiveNumericVariables(keys) {
    const numeric = keys
        .map((value) => Number.parseInt(value, 10))
        .filter((value, index) => Number.isFinite(value) && String(value) === String(keys[index]));
    if (numeric.length === 0) return null;
    const sorted = [...new Set(numeric)].sort((a, b) => a - b);
    for (let index = 0; index < sorted.length; index += 1) {
        if (sorted[index] !== index + 1) {
            return 'WhatsApp variables must be numbered consecutively starting at {{1}}.';
        }
    }
    return null;
}

export function normalizeWhatsAppTemplateName(value = '') {
    return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
}

function escapeRegExp(value = '') {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function deriveNextTemplateCopyName(name = '', existingNames = []) {
    const normalizedName = normalizeText(name);
    const baseName = normalizedName.replace(/_v\d+$/, '') || normalizedName;
    const seenNames = new Set(
        [...(existingNames || []).map((entry) => normalizeText(entry)), normalizedName].filter(Boolean)
    );
    const suffixPattern = new RegExp(`^${escapeRegExp(baseName)}_v(\\d+)$`);
    const sourceMatch = normalizedName.match(suffixPattern);
    let nextVersion = sourceMatch ? ((Number.parseInt(sourceMatch[1], 10) || 1) + 1) : 2;

    seenNames.forEach((entry) => {
        const match = entry.match(suffixPattern);
        if (!match) return;
        nextVersion = Math.max(nextVersion, (Number.parseInt(match[1], 10) || 1) + 1);
    });

    while (seenNames.has(`${baseName}_v${nextVersion}`)) nextVersion += 1;

    return {
        baseName,
        name: `${baseName}_v${nextVersion}`,
        version: nextVersion,
    };
}

export function insertNextTemplateVariable(value = '', { start = null, end = null } = {}) {
    const source = typeof value === 'string' ? value : '';
    const nextNumber = [...source.matchAll(/\{\{\s*(\d+)\s*\}\}/g)]
        .reduce((max, match) => Math.max(max, Number.parseInt(match[1], 10) || 0), 0) + 1;
    const token = `{{${nextNumber}}}`;
    const insertionStart = Number.isInteger(start) ? Math.max(0, Math.min(start, source.length)) : source.length;
    const insertionEnd = Number.isInteger(end) ? Math.max(insertionStart, Math.min(end, source.length)) : insertionStart;
    const nextValue = `${source.slice(0, insertionStart)}${token}${source.slice(insertionEnd)}`;
    const nextCursor = insertionStart + token.length;
    return {
        token,
        value: nextValue,
        nextSelectionStart: nextCursor,
        nextSelectionEnd: nextCursor,
    };
}

export function validateMessageTemplate(template, { forSubmit = false } = {}) {
    const errors = [];
    const channel = normalizeText(template?.channel).toLowerCase();
    const name = normalizeText(template?.name);
    const displayName = normalizeText(template?.display_name ?? template?.displayName);
    const body = typeof template?.body === 'string' ? template.body : '';
    const footer = typeof template?.footer === 'string' ? template.footer : '';
    const header = toJsonObject(template?.header);
    const buttons = toButtonArray(template?.buttons);
    const variables = normalizeVariables(template?.variables);

    if (!MESSAGE_TEMPLATE_CHANNELS.includes(channel)) {
        errors.push('channel must be one of whatsapp, sms, or email.');
    }

    if (!name) {
        errors.push('name is required.');
    }
    if (displayName.length > 80) {
        errors.push('Display name must be 80 characters or fewer.');
    }

    if (channel === 'whatsapp') {
        if (!WHATSAPP_NAME_PATTERN.test(name)) {
            errors.push('WhatsApp template names may only contain lowercase letters, digits, and underscores.');
        }
        if (body.length > 1024) {
            errors.push('WhatsApp body must be 1024 characters or fewer.');
        }
        if (forSubmit && !normalizeText(body)) {
            errors.push('WhatsApp body is required before submit.');
        }

        const headerType = normalizeText(header?.type).toLowerCase();
        const headerText = typeof header?.text === 'string' ? header.text : '';
        if (headerType === 'text' && headerText.length > 60) {
            errors.push('WhatsApp header text must be 60 characters or fewer.');
        }
        if (footer.length > 60) {
            errors.push('WhatsApp footer must be 60 characters or fewer.');
        }
        if (buttons.length > 10) {
            errors.push('WhatsApp templates support at most 10 buttons.');
        }
        buttons.forEach((button, index) => {
            const type = normalizeText(button?.type).toLowerCase();
            const text = typeof button?.text === 'string' ? button.text : '';
            if (!['quick_reply', 'phone', 'url', 'copy_code'].includes(type)) {
                errors.push(`Button ${index + 1} has an unsupported type.`);
                return;
            }
            if ((type === 'quick_reply' || type === 'phone' || type === 'url') && text.length > 25) {
                errors.push(`Button ${index + 1} text must be 25 characters or fewer.`);
            }
            if (type === 'quick_reply' && !normalizeText(text)) {
                errors.push(`Button ${index + 1} quick reply text is required.`);
            }
            if (type === 'phone' && !normalizeText(button?.phone || button?.phone_number)) {
                errors.push(`Button ${index + 1} phone number is required.`);
            }
            if (type === 'url' && !normalizeText(button?.url)) {
                errors.push(`Button ${index + 1} URL is required.`);
            }
        });

        const referencedKeys = listVariablesInTemplate({ body, header, footer, buttons });
        const consecutiveError = validateConsecutiveNumericVariables(referencedKeys);
        if (consecutiveError) {
            errors.push(consecutiveError);
        }
        referencedKeys.forEach((key) => {
            const sample = normalizeText(variables?.[key]?.sample);
            if (!sample) {
                errors.push(`Variable {{${key}}} is missing a sample value.`);
            }
        });
    }

    return {
        valid: errors.length === 0,
        errors,
        normalized: {
            channel,
            name,
            displayName,
            category: normalizeText(template?.category).toLowerCase() || null,
            language: normalizeText(template?.language).toLowerCase() || 'en',
            header,
            body,
            footer,
            buttons,
            variables,
            emailSubject: typeof template?.email_subject === 'string'
                ? template.email_subject
                : (typeof template?.emailSubject === 'string' ? template.emailSubject : ''),
            status: resolveTemplateStatus({ channel, status: template?.status }),
        },
    };
}

function variableSamplesForTwilio(variables = {}) {
    return Object.fromEntries(
        Object.entries(normalizeVariables(variables))
            .filter(([key]) => key)
            .map(([key, value]) => [key, value.sample || 'sample'])
    );
}

function buildTwilioCardActions(buttons = []) {
    return toButtonArray(buttons).map((button) => {
        const type = normalizeText(button?.type).toLowerCase();
        if (type === 'quick_reply') {
            return { type: 'QUICK_REPLY', title: normalizeText(button?.text) };
        }
        if (type === 'phone') {
            return {
                type: 'PHONE_NUMBER',
                title: normalizeText(button?.text),
                phone_number: normalizeText(button?.phone || button?.phone_number),
            };
        }
        if (type === 'url') {
            return {
                type: 'URL',
                title: normalizeText(button?.text),
                url: normalizeText(button?.url),
                dynamic_suffix: normalizeText(button?.dynamicSuffix || button?.dynamic_suffix),
            };
        }
        if (type === 'copy_code') {
            return {
                type: 'COPY_CODE',
                title: normalizeText(button?.text),
                code: normalizeText(button?.code),
            };
        }
        return null;
    }).filter(Boolean);
}

export function resolveTwilioTemplateHeaderMedia(header = {}, bucket = MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET) {
    const candidate = normalizeText(header?.url || header?.storage_path || header?.path);
    if (!candidate) {
        return {
            url: '',
            requiresSignedUrl: false,
            bucket,
            objectPath: '',
        };
    }
    if (/^https?:\/\//i.test(candidate)) {
        return {
            url: candidate,
            requiresSignedUrl: false,
            bucket,
            objectPath: '',
        };
    }
    const normalizedPath = candidate.startsWith(`${bucket}/`)
        ? candidate.slice(bucket.length + 1)
        : candidate.replace(/^\/+/, '');
    return {
        url: '',
        requiresSignedUrl: true,
        bucket,
        objectPath: normalizedPath,
    };
}

export function buildTwilioContentPayload(template) {
    const validated = validateMessageTemplate(template, { forSubmit: true });
    if (!validated.valid) {
        return { ok: false, errors: validated.errors };
    }

    const { normalized } = validated;
    const payload = {
        friendly_name: normalized.name,
        language: normalized.language,
        variables: variableSamplesForTwilio(normalized.variables),
        types: {
            'twilio/text': {
                body: normalized.body,
            },
        },
    };

    const headerType = normalizeText(normalized.header?.type).toLowerCase();
    const hasCardContent = headerType !== 'none'
        || Boolean(normalizeText(normalized.footer))
        || normalized.buttons.length > 0;

    if (hasCardContent) {
        const card = {
            body: normalized.body,
            actions: buildTwilioCardActions(normalized.buttons),
        };
        if (headerType === 'text') {
            card.title = normalizeText(normalized.header?.text);
        } else if (['image', 'video', 'document'].includes(headerType)) {
            const media = resolveTwilioTemplateHeaderMedia(normalized.header);
            card.media = [{
                type: headerType,
                url: media.url || media.objectPath,
            }].filter((item) => item.url);
        }
        if (normalizeText(normalized.footer)) {
            card.footer = normalizeText(normalized.footer);
        }
        payload.types['twilio/card'] = card;
    }

    return {
        ok: true,
        payload,
        approvalCreate: {
            name: normalized.name,
            category: MESSAGE_TEMPLATE_CATEGORIES.includes(normalized.category || '')
                ? normalized.category
                : 'utility',
        },
    };
}

export function mapTwilioApprovalToTemplateStatus({ channel = 'whatsapp', status, rejectionReason = null } = {}) {
    const normalizedChannel = normalizeText(channel).toLowerCase();
    if (normalizedChannel !== 'whatsapp') {
        return { status: 'approved', rejectionReason: null };
    }

    const normalized = normalizeText(status).toLowerCase();
    if (!normalized) {
        return { status: 'pending', rejectionReason: null };
    }
    if (['approved', 'live'].includes(normalized)) {
        return { status: 'approved', rejectionReason: null };
    }
    if (['rejected', 'denied', 'failed'].includes(normalized)) {
        return { status: 'rejected', rejectionReason: normalizeText(rejectionReason) || null };
    }
    if (['paused'].includes(normalized)) {
        return { status: 'paused', rejectionReason: null };
    }
    if (['disabled'].includes(normalized)) {
        return { status: 'disabled', rejectionReason: null };
    }
    return { status: 'pending', rejectionReason: null };
}

function extractTwilioBody(types = {}) {
    const textType = types?.['twilio/text'];
    if (isPlainObject(textType) && typeof textType.body === 'string') {
        return textType.body;
    }
    const cardType = types?.['twilio/card'];
    if (isPlainObject(cardType) && typeof cardType.body === 'string') {
        return cardType.body;
    }
    return '';
}

function mapTwilioCardToHeader(card = {}) {
      if (!isPlainObject(card)) return { type: 'none' };
      if (typeof card.title === 'string' && card.title.trim()) {
          return { type: 'text', text: card.title };
      }
      const media = Array.isArray(card.media) ? card.media : [];
      const firstMedia = media[0];
      if (isPlainObject(firstMedia) && typeof firstMedia.url === 'string' && firstMedia.url.trim()) {
          const mediaType = normalizeText(firstMedia.type).toLowerCase() || 'image';
          return { type: mediaType, storage_path: firstMedia.url };
      }
      return { type: 'none' };
}

function mapTwilioCardActionsToButtons(actions = []) {
      if (!Array.isArray(actions)) return [];
      return actions.map((action) => {
          const type = normalizeText(action?.type).toUpperCase();
          if (type === 'QUICK_REPLY') {
              return { type: 'quick_reply', text: normalizeText(action?.title) };
          }
          if (type === 'PHONE_NUMBER') {
              return {
                  type: 'phone',
                  text: normalizeText(action?.title),
                  phone: normalizeText(action?.phone_number),
              };
          }
          if (type === 'URL') {
              return {
                  type: 'url',
                  text: normalizeText(action?.title),
                  url: normalizeText(action?.url),
                  dynamicSuffix: normalizeText(action?.dynamic_suffix),
              };
          }
          if (type === 'COPY_CODE') {
              return {
                  type: 'copy_code',
                  text: normalizeText(action?.title),
                  code: normalizeText(action?.code),
              };
          }
          return null;
      }).filter(Boolean);
}

export function mapTwilioContentRecordToTemplatePatch(
  content,
  { orgId, now = new Date().toISOString(), twilioAccountSid = null } = {}
) {
    const approval = content?.approval_requests?.whatsapp || content?.approval_requests?.WhatsApp || {};
    const statusResult = mapTwilioApprovalToTemplateStatus({
        channel: 'whatsapp',
        status: approval?.status,
        rejectionReason: approval?.rejection_reason || approval?.rejectionReason,
    });

    const card = isPlainObject(content?.types?.['twilio/card']) ? content.types['twilio/card'] : {};

    return {
        org_id: orgId,
        channel: 'whatsapp',
        name: normalizeText(content?.friendly_name || content?.name || content?.sid),
        category: normalizeText(content?.whatsapp?.category || content?.category).toLowerCase() || 'utility',
        language: normalizeText(content?.language).toLowerCase() || 'en',
        header: Object.keys(toJsonObject(content?.header, {})).length ? toJsonObject(content?.header, {}) : mapTwilioCardToHeader(card),
        body: extractTwilioBody(content?.types || {}),
        footer: normalizeText(content?.footer) || normalizeText(card?.footer),
        buttons: Array.isArray(content?.buttons) && content.buttons.length > 0
          ? content.buttons
          : mapTwilioCardActionsToButtons(card?.actions),
        variables: normalizeVariables(content?.variables),
        status: statusResult.status,
        rejection_reason: statusResult.rejectionReason,
        twilio_content_sid: normalizeText(content?.sid),
        twilio_account_sid: normalizeText(twilioAccountSid) || null,
        last_synced_at: now,
    };
}

export function buildTemplateEvent({
    templateId,
    orgId,
    actorId = null,
    action,
    fromStatus = null,
    toStatus = null,
    detail = {},
}) {
    return {
        template_id: templateId,
        org_id: orgId,
        actor_id: actorId,
        action,
        from_status: fromStatus,
        to_status: toStatus,
        detail: isPlainObject(detail) ? detail : {},
    };
}

const WHATSAPP_STATUS_NOTIFICATION_META = {
    approved: {
        eventType: 'communications.template.whatsapp.approved',
        workflowKey: 'bl373-template-whatsapp-approved',
        severity: 'info',
        verb: 'approved',
    },
    rejected: {
        eventType: 'communications.template.whatsapp.rejected',
        workflowKey: 'bl373-template-whatsapp-rejected',
        severity: 'warning',
        verb: 'rejected',
    },
    paused: {
        eventType: 'communications.template.whatsapp.paused',
        workflowKey: 'bl373-template-whatsapp-paused',
        severity: 'warning',
        verb: 'paused',
    },
    disabled: {
        eventType: 'communications.template.whatsapp.disabled',
        workflowKey: 'bl373-template-whatsapp-disabled',
        severity: 'warning',
        verb: 'disabled',
    },
};

export function buildWhatsappTemplateStatusNotification({
    templateId,
    templateName,
    status,
    rejectionReason = null,
    basePath = '/settings/email-channel?tab=templates',
} = {}) {
    const normalizedStatus = normalizeText(status).toLowerCase();
    const notificationMeta = WHATSAPP_STATUS_NOTIFICATION_META[normalizedStatus];
    if (!notificationMeta || !templateId) return null;

    const normalizedName = normalizeText(templateName) || 'unnamed_template';
    const normalizedReason = normalizeText(rejectionReason);
    const includeReasonInTitle = normalizedStatus === 'rejected' && normalizedReason;
    const title = includeReasonInTitle
        ? `WhatsApp template ${notificationMeta.verb}: ${normalizedName} — ${normalizedReason}`
        : `WhatsApp template ${notificationMeta.verb}: ${normalizedName}`;
    const body = includeReasonInTitle
        ? `Meta reviewed template "${normalizedName}" and marked it ${normalizedStatus}. Reason: ${normalizedReason}.`
        : `Meta reviewed template "${normalizedName}" and marked it ${normalizedStatus}.`;
    const templateUrl = `${basePath}&template=${encodeURIComponent(templateId)}`;

    return {
        eventType: notificationMeta.eventType,
        workflowKey: notificationMeta.workflowKey,
        severity: notificationMeta.severity,
        status: normalizedStatus,
        payload: {
            title,
            body,
            severity: notificationMeta.severity,
            templateId,
            templateName: normalizedName,
            status: normalizedStatus,
            rejectionReason: normalizedReason || null,
            url: templateUrl,
            link: templateUrl,
            deepLink: templateUrl,
        },
    };
}
